import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../features/auth/AuthProvider';
import { useMediaQuery } from '../hooks/useMediaQuery';
import type { IconName } from './Icon';
import { Icon } from './Icon';
import { ThemeSwitcher } from './ThemeSwitcher';
import styles from './Sidebar.module.scss';

export const SIDEBAR_WIDTH = 240;

type NavItem = {
  readonly label: string;
  readonly path: string;
  readonly icon: IconName;
  readonly permission?: string;
};

type SidebarProps = {
  readonly open: boolean;
  readonly collapsed: boolean;
  readonly onClose: () => void;
  readonly onToggleCollapsed: () => void;
};

export function Sidebar({ open, collapsed, onClose, onToggleCollapsed }: SidebarProps) {
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width: 899px)');
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const userPermissions = user?.permissions ?? [];
  const navItems: readonly NavItem[] = [
    { label: t('nav.dashboard'), path: '/', icon: 'dashboard' },
    { label: t('nav.clients'), path: '/clients', icon: 'people' },
    { label: t('nav.chats'), path: '/chats', icon: 'inbox' },
    { label: t('nav.activity'), path: '/activity', icon: 'history' },
    { label: t('nav.testimonials'), path: '/testimonials', icon: 'star' },
    { label: t('nav.tasks'), path: '/tasks', icon: 'task', permission: 'tasks:view' },
    { label: t('nav.bankAccounts'), path: '/bank-accounts', icon: 'payment' },
    { label: t('nav.pointsRewards'), path: '/points-rewards', icon: 'trending-up', permission: 'points:manage' },
    { label: 'Блог', path: '/help-articles', icon: 'history', permission: 'help_articles:manage' },
    { label: t('nav.team'), path: '/team', icon: 'people', permission: 'users:manage' },
    { label: t('nav.roles'), path: '/roles', icon: 'lock', permission: 'roles:view' },
  ];

  const visibleItems = navItems.filter(
    (item) => !item.permission || userPermissions.includes(item.permission),
  );

  function isActive(path: string): boolean {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  }

  const content = (
    <div className={styles.content}>
      <div className={styles.brand}>
        <div className={styles.brandCopy}>
          <span className={styles.brandLabel}>White&Flex</span>
          <span className={styles.brandTitle}>{t('nav.adminPanel')}</span>
        </div>
        {!isMobile && (
          <button
            type="button"
            className={styles.collapseButton}
            aria-label={t(collapsed ? 'common.expandSidebar' : 'common.collapseSidebar')}
            aria-expanded={!collapsed}
            onClick={onToggleCollapsed}
          >
            <Icon name="arrow-left" size={18} className={styles.collapseIcon} />
          </button>
        )}
      </div>

      <ul className={styles.nav}>
        {visibleItems.map((item) => {
          const active = isActive(item.path);
          return (
            <li key={item.path}>
              <button
                className={`${styles.navItem} ${active ? styles.active : ''}`}
                aria-label={collapsed && !isMobile ? item.label : undefined}
                title={collapsed && !isMobile ? item.label : undefined}
                onClick={() => {
                  navigate(item.path);
                  if (isMobile) onClose();
                }}
              >
                <span className={styles.navIcon}>
                  <Icon name={item.icon} size={18} />
                </span>
                <span className={styles.navLabel}>{item.label}</span>
              </button>
            </li>
          );
        })}
      </ul>

      <div className={styles.footer}>
        <ThemeSwitcher />
      </div>
    </div>
  );

  if (isMobile) {
    return open ? (
      <>
        <div className={styles.overlay} onClick={onClose} />
        <aside className={`${styles.drawer} ${styles.open}`}>{content}</aside>
      </>
    ) : null;
  }

  return (
    <aside
      className={`${styles.drawer} ${styles.persistent} ${collapsed ? styles.collapsed : ''}`}
    >
      {content}
    </aside>
  );
}
